"use client";
import React, { MouseEvent, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Button, ButtonBar, ButtonGroup, Main, Module, ThemedPage } from '@/components';
import { REQUEST_PASSWORD_RESET } from '@/gql/queries/mutations/requestPasswordReset';

import { TextInput } from '../../../components/forms';
import PageHeading from '../../../components/page-heading';
import UserError, { UserErrors } from '../../../components/user-error';

const RequestPasswordResetEmailForm = () => {
  const inputEmailRef = useRef<HTMLInputElement>(null);
  const [errorState, setErrorState] = useState<UserErrors>([]);
  const { push } = useRouter();

  const [sendPasswordReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET);

  const onSendResetClicked = (e: MouseEvent<HTMLButtonElement>) => {
    if (!inputEmailRef?.current?.value) return;
    setErrorState([]);
    sendPasswordReset({
      variables: { email: inputEmailRef.current.value }, onError: () => {
        setErrorState(['Failed to send password reset email. Please try again later.']);
      }, onCompleted: () => {
        push('/password-reset/sent');
      }
    })
    e.preventDefault();
  };

  return (
    <ThemedPage>
      <Main small>
        <PageHeading>Reset your password</PageHeading>
        <Module>
          <p>
            If you have forgotten your password, we can send you an email to set
            a new password. Enter your email address below.{' '}
            <Link href="/sign-in">Return to sign-in</Link>.
          </p>
        </Module>
        <UserError errors={errorState} />
        <form>
          <Module>
            <TextInput
              id="email"
              type="email"
              ref={inputEmailRef}
              autoCapitalize="none"
              label="Email address:"
            />
          </Module>
          <ButtonBar>
            <ButtonGroup align="right">
              <Button
                icon={faEnvelope}
                onClick={onSendResetClicked}
                rightAlign
                data-testid="request-reset-button"
                disabled={loading}
              >
                Send password reset email
              </Button>
            </ButtonGroup>
          </ButtonBar>
        </form>
      </Main>
    </ThemedPage>
  );
};

export default RequestPasswordResetEmailForm;
